/*
* Association components client questions report view for authenticated users
*/

import '../../branding/report-pdf.css';
import '../pdf.css';

import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Alert, Button, ButtonToolbar, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import Branding from '../../branding/branding';
import * as AppComp from '../../common/app-component-base';
import * as AppUtilities from '../../common/apputilities';
import { BootstrapColXl as Col } from '../../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../../common/formatters';
import { GridUtilities } from '../../common/grid/gridutilities';
import { NavigationLinks } from '../../common/navigation';
import ApiEndpoints from '../../config/apiendpoints';
import { AppImages } from '../../config/images';
import * as ReduxQueries from '../../queries';
import * as ReportCharts from '../charts';
import { AppDataConstants } from '../../common/app-data-constants';
import * as ReportLogic from '../logic';
import * as ReportCommon from './../report-common';
import { Render as ReportPageComponentClientQuestions } from './pages/report-page-component-client-questions';
import * as ReportComponents from './report-components';
import { isLevel3Report } from '../../order/utils';

export interface IAssociationClientQuestionsReportPdfProps extends AppComp.IAppComponentProps {
    association?: any;
    associationId?: any;
    currentOrder?: any;
    currentOrderChart?: any;
    inventory?: any;
    scenarios?: any;
    userResumes?: any;
    navigateTo?: any;
}

export interface IAssociationClientQuestionsReportPdfState extends AppComp.IAppComponentState {
    formChanges?: any;
    inventoryChanges?: any;
    reportPageCount: number;
}

class AssociationClientQuestionsReportPdf extends AppComp.AppComponent<IAssociationClientQuestionsReportPdfProps, IAssociationClientQuestionsReportPdfState> {
    public static readonly scrollToConfig = {
        align: 'top',
        ease: 'out-circ',
        duration: 550,
        offset: -55
    };

    public originalExtendedInventoryCache: Array<any> = [];
    public sectionPages: Array<any> = [];
    public pdfExportComponent: any = undefined;

    public constructor(props: any) {
        super(props, 'app.report-association.');

        this.state = {
            formChanges: {},
            inventoryChanges: [],
            reportPageCount: 0,
        };

        ReportCharts.setHighchartOptions();
    }

    public extendCurrentOrderUsers(currentOrder: any, userResumes: Array<any>) {
        const managerObjId = _.get(currentOrder, 'user.userObjectId', '');
        const manager = _.find(userResumes, ['user.userObjectId', managerObjId]);

        const siteInspectorObjId = _.get(currentOrder, 'siteInspector.userObjectId', '');
        const siteInspector = _.find(userResumes, ['user.userObjectId', siteInspectorObjId]);

        return {
            manager: manager ? _.cloneDeep(manager) : {},
            siteInspector: siteInspector ? _.cloneDeep(siteInspector) : {},
        };
    }

    public extendAssociationData(record: any) {
        if (!record) { return; }

        let result = _.cloneDeep(record);
        const invSubgroups = AppUtilities.getArray(record, 'inventorySubgroups');
        if (AppUtilities.isArrayWithLength(invSubgroups)) {
            // result.inventorySubgroups.push({ groupCode: '', title: '', label: this.appText('filter-option-any') });
            result.inventorySubgroups = result.inventorySubgroups.map(c => {
                if (!c.label) {
                    c.label = Formatters.composeInventorySubgroupLabel(c, true); // c.label = `${c.groupCode} - ${c.title}`;
                }

                return c;
            });
        }

        return result;
    }

    public extendInventoryData(inventory: Array<any>, inventoryChanges: Array<any>, association: any) {
        // Extend grid row data
        if (!AppUtilities.isArrayWithLength(inventory)) { return []; }

        const extended = inventory.map((r, i) => {
            let record = _.cloneDeep(r);

            record.avgCost = ReportLogic.calculateInventoryItemAvgCost(record);
            record.avgCost_sort = Number(record.avgCost);
            if (!record.avgCost) {
                GridUtilities.recordSetRowClass(record, 'row-no-avg-cost');
                record.usefulLife = AppDataConstants.UsefulLifeOptions.NotApplicable;
                record.remainingUsefulLife = 0;
            }

            const invSubgroups = AppUtilities.getArray(association, 'inventorySubgroups');
            record.Group = '';
            if (AppUtilities.isArrayWithLength(invSubgroups)) {
                let subgroupDef: any = _.find(invSubgroups, ['groupCode', _.get(record, 'subgroup', '')]);
                if (subgroupDef && subgroupDef.title) { record.Group = subgroupDef.title; }
            }

            record.usefulLife_sort = (AppUtilities.isNumeric(record.usefulLife)) ? Formatters.int(record.usefulLife) : 0;
            record.remainingUsefulLife_sort = (AppUtilities.isNumeric(record.remainingUsefulLife)) ? Formatters.int(record.remainingUsefulLife) : 0;

            record.included = _.get(record, 'included', (record.avgCost_sort !== 0 && record.usefulLife !== 'N/A') ? true : false);
            record.hypAvgCost = Formatters.float(_.get(record, 'hypAvgCost', record.avgCost_sort), 2);
            record.hypUsefulLife = Formatters.int(_.get(record, 'hypUsefulLife', record.usefulLife_sort));
            record.hypRemainingUsefulLife = Formatters.int(_.get(record, 'hypRemainingUsefulLife', record.remainingUsefulLife_sort));

            let invChanges = _.find(inventoryChanges, { inventoryId: record.inventoryId });
            if (invChanges) {
                record = _.merge(record, _.omit(invChanges, 'inventoryId'));
            }

            record.editing = record.included;

            return record;
        });

        if (this.originalExtendedInventoryCache.length === 0) { this.originalExtendedInventoryCache = _.cloneDeep(extended); }

        return extended;
    }

    public getFormData(currentOrderChart: any, currentOrder: any) {
        // currentOrderChart or currentOrder may be entirely missing when in this view.

        const result = {
            calculateYears: ReportCommon.CalculateYears, // Formatters.getInt(currentOrderChart, 'years', ReportCommon.CalculateYears),

            currentAnnualIncrease: Formatters.getFloat(currentOrderChart, 'currentAnnualIncrease', 5, 1, 0.03),
            currentMRCVal: Formatters.getFloat(currentOrderChart, 'currentMRCVal', 2),
            currentSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'currentSecondaryAnnualIncrease', 5, 1, 0.03),
            currentYears: Formatters.getInt(currentOrderChart, 'currentYears', ReportCommon.CalculateYears),

            minimumAnnualIncrease: Formatters.getFloat(currentOrderChart, 'minimumAnnualIncrease', 5, 1, 0.03),
            minimumMRCVal: Formatters.getFloat(currentOrderChart, 'minimumMRCVal', 2),
            minimumSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'minimumSecondaryAnnualIncrease', 5, 1, 0.03),
            minimumYears: Formatters.getInt(currentOrderChart, 'minimumYears', ReportCommon.CalculateYears),

            recommendedAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedAnnualIncrease', 5, 1, 0.03),
            recommendedMRCVal: Formatters.getFloat(currentOrderChart, 'recommendedMRCVal', 2),
            recommendedSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedSecondaryAnnualIncrease', 5, 1, 0.03),
            recommendedYears: Formatters.getInt(currentOrderChart, 'recommendedYears', ReportCommon.CalculateYears),

            // aka idealBalance
            currentFFB: Formatters.getFloat(currentOrderChart, 'ffb', 2) ? Formatters.getFloat(currentOrderChart, 'ffb', 2) : Formatters.getFloat(currentOrderChart, 'begBalance', 2),

            draft: Formatters.getBool(currentOrderChart, 'draft'),
            showMinimum: Formatters.getBool(currentOrderChart, 'showMinimum'),
            showRecommended: Formatters.getBool(currentOrderChart, 'showRecommended'),
            groupComponents: Formatters.getBool(currentOrderChart, 'groupComponents'),
            groupComponentsPictureSection: Formatters.getBool(currentOrderChart, 'groupComponentsPictureSection'),
            hideFooter: Formatters.getBool(currentOrderChart, 'hideFooter'),
            hideComponentEvaluation: Formatters.getBool(currentOrderChart, 'hideComponentEvaluation'),

            inflation: Formatters.getFloat(currentOrderChart, 'inflation', 5, 1),
            interest: Formatters.getFloat(currentOrderChart, 'interest', 5, 1),
            perFirstYear: Formatters.getInt(currentOrderChart, 'perFirstYear', 12),

            begBalance: Formatters.getFloat(currentOrderChart, 'begBalance', 2),
            beginYear: parseInt(Formatters.getStr(currentOrderChart, 'beginYear', new Date().getFullYear().toString()), 10),
            calculateYearsShort: 29,
            mrcValue: Formatters.getFloat(currentOrderChart, 'recommendedMRCVal', Formatters.getFloat(currentOrder, 'recommendedMRC')).toFixed(2),
            specialAssessment: Formatters.getFloat(currentOrderChart, 'sa').toFixed(2),
            years: Formatters.getInt(currentOrderChart, 'recommendedYears').toFixed(0),
        };

        return _.merge(result, this.state.formChanges);
    }

    public handleAdjustmentFormInputChange(n: any, v: any) {
        let changes = Object.assign({}, this.state.formChanges);
        changes[n] = Number(v);

        this.setState({ formChanges: changes });
    }

    public handleAdjustmentFormSubmit(e: any) {
        // console.log('handleAdjustmentFormSubmit', e);
    }

    public handleItemChange(event: any) {
        const itemField = event.field;
        const itemValueNew = event.value;
        const itemValueOld = event.dataItem[itemField];
        const itemId = event.dataItem.inventoryId;
        const origItem = _.find(this.originalExtendedInventoryCache, { 'inventoryId': itemId });
        const origValue = _.get(origItem, itemField);

        // console.log(`handleItemChange id ${itemId} field ${itemField} changed from ${itemValueOld} to ${itemValueNew}. orig ${origValue}`, origItem);

        let inv = _.cloneDeep(this.state.inventoryChanges);
        if (!_.find(inv, { 'inventoryId': itemId })) {
            const newItem = { inventoryId: itemId, };
            inv.push(newItem);
        }

        let item = _.find(inv, { 'inventoryId': itemId });
        if (item && _.get(item, itemField) !== itemValueNew) {
            if (itemValueNew === origValue) {
                // New value same as original, delete any change
                _.unset(item, itemField);
            } else {
                // New value differs from original
                _.set(item, itemField, itemValueNew);
            }

            if (_.keys(_.omit(item, 'inventoryId')).length === 0) {
                // No more changes for this item, remove it
                inv = _.reject(inv, { inventoryId: itemId });
            }

            _.set(event.dataItem, itemField, itemValueNew);

            this.setState({ inventoryChanges: inv });
        }
    }

    public getIsHypothetical() {
        const isHypothetical = (this.state.inventoryChanges.length === 0 && _.keys(this.state.formChanges).length === 0) ? false : true;

        return isHypothetical;
    }

    public getCompanyLogoByAssociation(association: any, location: string = Branding.appBrandLogoLocations.reportFooter) {
        const assocCompanyBrandKey = _.get(association, 'company.brandKey', Branding.appBrandKeys.complexSolutions);

        const path = `${assocCompanyBrandKey}.logo.${location}`;
        const result = _.get(AppImages, path, '');

        return result;
    }

    public getBrandingCssNameByAssociation(association: any) {
        const assocCompanyBrandKey = _.get(association, 'company.brandKey', Branding.appBrandKeys.complexSolutions);

        return assocCompanyBrandKey;
    }

    public registerSectionPageRef = (element: any, sectionName: string, pageIndex: number = 0) => {
        const pageKey = `${sectionName}-${pageIndex}`;

        const existing = _.find(this.sectionPages, ['pageKey', pageKey]);
        if (existing) {
            if (_.get(existing, 'elementRef') !== element) {
                _.set(existing, 'elementRef', element);
            }
        }
    }

    public registerSectionPage = (sectionName: string, pageTitle: string = '', pageIndex: number = 0, includeInPageCount: boolean = true): number => {
        const eligiblePages = _.filter(this.sectionPages, (o) => _.get(o, 'includeInPageCount', true));
        const pageCount = _.size(eligiblePages);

        const pageNumber = (includeInPageCount) ? pageCount + 1 : 0;
        const pageKey = `${sectionName}-${pageIndex}`;

        const existing = _.find(this.sectionPages, ['pageKey', pageKey]);
        if (!existing) {
            const page = {
                elementRef: undefined,
                includeInPageCount: includeInPageCount,
                pageKey: pageKey,
                pageNumber: pageNumber,
                pageTitle: pageTitle,
                sectionName: sectionName,
            };

            this.sectionPages.push(page);

            if (includeInPageCount) {
                this.setState({ reportPageCount: pageCount + 1 });
            }
        } else {
            return existing.pageNumber;
        }

        return pageNumber;
    }

    public getFirstPageOfSection = (secName: string): any => _(this.sectionPages).sortBy('pageNumber').find(['sectionName', secName]);

    public getPageNum = (secName: string) => _.get(this.getFirstPageOfSection(secName), 'pageNumber');

    public getPageRef = (secName: string) => _.get(this.getFirstPageOfSection(secName), 'elementRef');

    // HANDLERS
    public handlePrintPageButton(e: any) {
        window.print();
    }

    public handleViewAssociationButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationDetail(associationId));
    }

    public handleViewInventoryButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationInventory(associationId));
    }

    // RENDERERS
    public renderHeaderButtonToolbar = () => {
        let buttonProps = {
            bsSize: 'sm',
            bsStyle: 'info',
        };

        return (
            <div className="header-print-buttontoolbar-report">
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={12}>
                            <ButtonToolbar>
                                <Button {...buttonProps} bsStyle="info" onClick={(e) => { this.handlePrintPageButton(e); }}>
                                    <i className="glyphicon glyphicon-print" /> {this.appTextCommon('buttons.print-document')}
                                </Button>
                                <Button {...buttonProps} onClick={e => { this.handleViewAssociationButton(e); }}>
                                    <i className="glyphicon glyphicon-eye-open" /> {this.appTextCommon('buttons.view-association')}
                                </Button>
                                <Button {...buttonProps} onClick={e => { this.handleViewInventoryButton(e); }}>
                                    <i className="glyphicon glyphicon-th-list" /> {this.appTextCommon('buttons.view-inventory')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public render() {
        const { currentOrder, currentOrderChart, associationId } = this.props;

        const association = this.extendAssociationData(this.props.association);
        const inventory: Array<any> = this.extendInventoryData(this.props.inventory, this.state.inventoryChanges, association);

        let cloneCurrentOrderChart = _.cloneDeep(currentOrderChart);
        let cloneCurrentOrder = _.cloneDeep(currentOrder);

        const isLoading = Number(Formatters.getInt(association, 'associationId')) !== Number(associationId);
        const hasComponentsWithNegativeRUL = false;

        // const invFiltered = _.filter(inventory, (o) => Formatters.getBool(o, 'printClientQuestions'));

        let noAssocInventoryData = _.isEmpty(inventory) || _.isEmpty(association);

        let page = (<div />);
        let brandingCssName = '';

        if (!isLoading) {
            if (noAssocInventoryData) {
                page = (
                    <div>
                        <Row>
                            <Col xs={4}>
                                <div className="association-inventory-alert-component-negative-rul">
                                    <Alert bsStyle="warning">
                                        <h4><i className="fa fa-exclamation-triangle" /> {this.appText('alerts.warn-association-inventory-data-missing.title')}</h4>
                                        <p>{this.appText('alerts.warn-association-inventory-data-missing.body')}</p>
                                    </Alert>
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            } else {
                let currentOrderUsers = this.extendCurrentOrderUsers(cloneCurrentOrder, this.props.userResumes);

                const isHypothetical = this.getIsHypothetical();
                const formData = this.getFormData(cloneCurrentOrderChart, cloneCurrentOrder);

                const mergedCurrentOrderChart = _.merge({}, cloneCurrentOrderChart, formData);

                const orderChartBeginYear = _.get(mergedCurrentOrderChart, 'beginYear');
                const assocFiscalYearEnd = _.get(association, 'details.fiscalYearEnd');
                const perFirstYear = Number(_.get(mergedCurrentOrderChart, 'perFirstYear', 12));
                const fyeDateStr = `${orderChartBeginYear}/${assocFiscalYearEnd}`;
                const dateEndAssocFYE = new Date(fyeDateStr);
                const reportPeriodEndDateMoment = moment(dateEndAssocFYE).toObject();
                const reportPeriodEndDate = moment(reportPeriodEndDateMoment); // .subtract(1, 'day').toObject();
                const reportPeriodStartDate = moment(reportPeriodEndDateMoment).subtract(perFirstYear, 'month').add(1, 'day').toObject();

                // const calculationResult = ReportLogic.computeFundingCalculations(mergedCurrentOrderChart, cloneCurrentOrder, inventory, ReportCommon.CalculateYears);
                const calculationResult = ReportLogic.computeFundingCalculations(mergedCurrentOrderChart, cloneCurrentOrder, inventory, ReportCommon.CalculateYears);

                const calculations = AppUtilities.getArray(calculationResult, 'recordSet');
                const invMap = AppUtilities.getArray(calculationResult, 'invMap');

                const percentFunded = ReportLogic.getPercentFunded(calculations, isHypothetical);

                // REPORT PAGES CONFIGURATION
                const pageConfiguration = {
                    maxTableRowsPerPage: 24, // Causes page breaks if a table exceeds this # rows
                    maxTableRowsPerFirstPage: 18, // Causes page breaks if a table exceeds this # rows
                    maxColumnsPerTable: 3, // Used on yearly cash flow, etc
                    charactersPerLine: 110,
                    kendoCharts: {
                        renderAs: 'svg',
                    },
                    charts: {
                        xAxis: {
                            title: {
                                x: -40,
                                y: 20,
                            },
                        },
                        legend: {
                            verticalAlign: 'bottom',
                            align: 'center',
                            x: 0,
                            y: 0,
                        },
                        chart: {
                            height: 600
                        }
                    }
                };

                // YEARLY SUMMARY DATA
                const yearlySummaryData = ReportCharts.getYearlySummaryGridData((e) => this.appText(e), calculations, formData.beginYear, formData.calculateYears, isHypothetical);

                // COMPONENT EVALUATIONS
                const isComponentEvaluationHidden = (_.get(mergedCurrentOrderChart, 'hideComponentEvaluation', false)) ? true : false;
                const hideComponentEvaluation = (isLevel3Report(currentOrder) || isComponentEvaluationHidden) ? true : false;
                const showComponentEvaluation = (hideComponentEvaluation) ? false : true;

                // REPORT FOOTER
                const showReportPageFooter = !((_.get(mergedCurrentOrderChart, 'hideFooter', false)) ? true : false);

                // Limit to 30 years
                const yearlySummaryDataCorrected = _.take(yearlySummaryData, formData.calculateYears);
                const calculationsCorrected = _.take(calculations, formData.calculateYears);

                // FOOTER DATA
                const reportData = {
                    association: association,
                    showComponentEvaluation: showComponentEvaluation,
                    showFooterFull: showReportPageFooter,
                    showFooterAssocOrderInfo: false,
                    showFooterReportDate: true,
                    branding: {
                        // logoCoverPage: this.getCompanyLogo(currentOrder, Branding.appBrandLogoLocations.reportCoverPage),
                        // logoCoverPage: this.getCompanyLogo(currentOrder, Branding.appBrandLogoLocations.reportCoverPage),
                        logoCoverPage: this.getCompanyLogoByAssociation(association, Branding.appBrandLogoLocations.reportCoverPage),
                        logoFooter: this.getCompanyLogoByAssociation(association, Branding.appBrandLogoLocations.reportFooter),
                    },
                    calculations: calculationsCorrected, // calculations,
                    currentOrder: currentOrder,
                    currentOrderChart: mergedCurrentOrderChart, // currentOrderChart,
                    mergedCurrentOrderChart: mergedCurrentOrderChart,
                    currentOrderUsers: currentOrderUsers,
                    formData: formData,
                    inventory: invMap,
                    isHypothetical: isHypothetical,
                    percentFunded: percentFunded,
                    yearlySummaryData: yearlySummaryDataCorrected, // yearlySummaryData,
                    reportPeriodStartDate: reportPeriodStartDate,
                    reportPeriodEndDate: reportPeriodEndDate,
                };

                // console.log('reportData', reportData);
                // console.log(`report/pdf.tsx render`, { formData, cloneCurrentOrderChart, cloneCurrentOrder, calculationsCorrected, yearlySummaryData });

                brandingCssName = this.getBrandingCssNameByAssociation(association);

                const reportPagesProps = {
                    appText: (e) => this.appText(e),
                    pageConfiguration: pageConfiguration,
                    pageCount: this.state.reportPageCount,
                    refFunc: (e, s, i) => this.registerSectionPageRef(e, s, i),
                    registerFunc: (s, p, i, b) => this.registerSectionPage(s, p, i, b),
                    reportData: reportData,
                    scrollToFunc: undefined,
                    sectionPages: this.sectionPages,
                };

                const alertNoInventoryResults = (
                    <div>
                        <Row>
                            <Col xs={2}><div /></Col>
                            <Col xs={8}>
                                <div className="association-inventory-alert-component-negative-rul">
                                    <Alert bsStyle="warning">
                                        <h4><i className="fa fa-exclamation-triangle" /> {this.appText('alerts.warn-association-inventory-no-results.title')}</h4>
                                        <p>{this.appText('alerts.warn-association-inventory-no-results.body')}</p>
                                    </Alert>
                                </div>
                            </Col>
                            <Col xs={2}><div /></Col>
                        </Row>
                    </div>
                );

                const reportPages = (
                    <div>
                        <ReportPageComponentClientQuestions
                            className="pdf-page-component-client-questions"
                            key={ReportComponents.getRowKey(`section-0`, '0', '0')}
                            {...reportPagesProps}
                            maxTableRowsPerFirstPage={43}
                            maxTableRowsPerPage={43}
                            pageTitle={this.appText('page.component-client-questions')}
                        >
                            {alertNoInventoryResults}
                        </ReportPageComponentClientQuestions>
                    </div>
                );

                page = (
                    <div>
                        {this.renderHeaderButtonToolbar()}
                        <Row className="pdf-report-container">
                            <Col xs={12}>
                                <div className="pdf-report pdf-reserve-study-report center-block">
                                    <div className="pdf-reserve-study-report-container">
                                        {reportPages}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            }
        }

        const { ...rest } = this.props;
        return (
            <div className={brandingCssName}>
                <AppComp.RenderPageWithLayout branding={this.props.branding} pageName="" {...rest}>
                    {page}
                </AppComp.RenderPageWithLayout>
            </div>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getReportForAssociationUrl(props.match.params.associationId);

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getReportForAssociationRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props)
);

const mapStateToProps = (state, props) => {
    const assocId = Formatters.getInt(props, 'match.params.associationId');

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        association: state.entities.reportAssociation,
        associationId: assocId,

        currentOrder: state.entities.reportAssociationCurrentOrder,
        currentOrderChart: state.entities.reportAssociationCurrentOrderChart,
        inventory: state.entities.reportAssociationInventory,
        scenarios: state.entities.reportAssociationScenarios,
        userResumes: state.entities.reportAssociationUserResumes,
    });
};

const mapDispatchToProps = {
    navigateTo: (path) => push(path),
};

const AssociationClientQuestionsReportPdfContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(AssociationClientQuestionsReportPdf);

export default withRouter(AssociationClientQuestionsReportPdfContainer);



// WEBPACK FOOTER //
// ./src/report/pdf/client-questions-pdf.tsx